import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardHeader } from "reactstrap";

const ClientAcquisitionChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    if (!data || data.length === 0) {
        return (
            <Card
                className="neo-stat-card h-100 animate__animated animate__fadeIn"
                style={{
                    borderRadius: "16px",
                    border: "2px solid #482712",
                    boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                    background: "#fff",
                    fontFamily: "'Outfit', 'Inter', sans-serif"
                }}
            >
                <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <div
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    minWidth: "38px",
                                    borderRadius: "10px",
                                    background: "rgba(226, 102, 28, 0.08)",
                                    border: "1px solid rgba(226, 102, 28, 0.20)",
                                    color: "#e2661c",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <i className="mdi mdi-chart-bar font-size-18"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                                    Aquisição de Clientes
                                </h5>
                                <small className="text-muted font-size-11 fw-semibold d-block">
                                    Comparativo mensal de novos contratos vs cancelamentos (Últimos 6 meses)
                                </small>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "330px" }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-chart-histogram font-size-36 mb-2 d-block" style={{ color: "rgba(72, 39, 18, 0.2)" }}></i>
                        <span className="font-size-12 fw-semibold">Sem dados de aquisição de clientes registrados</span>
                    </div>
                </CardBody>
            </Card>
        );
    }

    const chartData = data;

    // Calcular valores de pico para a escala vertical
    const maxVal = Math.max(10, ...chartData.map(d => Math.max(d.newClients, d.cancelledClients))) * 1.15;

    // Cores outonais Premium
    const COLORS = {
        new: "#e2661c",         // Laranja destaque (Novos)
        cancelled: "#482712",   // Chocolate escuro (Cancelados)
        grid: "rgba(72, 39, 18, 0.06)",
        hoverBg: "rgba(72, 39, 18, 0.025)"
    };

    // Dimensões dos slots e barras (Base SVG 100x100)
    const totalSlots = chartData.length;
    const slotWidth = 100 / totalSlots; // e.g. 16.66
    const barWidth = 4.5;
    const gap = 1.0;

    return (
        <Card
            className="neo-stat-card h-100 animate__animated animate__fadeIn"
            style={{
                borderRadius: "16px",
                border: "2px solid #482712",
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{
                                width: "38px",
                                height: "38px",
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-chart-bar font-size-18"></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                                Aquisição de Clientes
                            </h5>
                            <small className="text-muted font-size-11 fw-semibold d-block">
                                Comparativo mensal de novos contratos vs cancelamentos (Últimos 6 meses)
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-1.5">
                            <span className="rounded" style={{ width: "12px", height: "12px", backgroundColor: COLORS.new }} />
                            <small className="fw-bold font-size-11" style={{ color: "#482712" }}>Novos</small>
                        </div>
                        <div className="d-flex align-items-center gap-1.5">
                            <span className="rounded" style={{ width: "12px", height: "12px", backgroundColor: COLORS.cancelled }} />
                            <small className="fw-bold font-size-11" style={{ color: "#482712" }}>Cancelados</small>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingTop: "50px", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "275px", position: "relative", minWidth: "550px", width: "100%", paddingRight: "10px" }}>
                        <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "52px" }}>

                            {/* Linhas de Grade Verticais e Horizontais */}
                            <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-between" style={{ pointerEvents: "none", bottom: "52px", height: "calc(100% - 52px)", zIndex: 1 }}>
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                            </div>

                            {/* SVG de Barras */}
                            <svg
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "calc(100% - 52px)",
                                    zIndex: 4
                                }}
                                preserveAspectRatio="none"
                                viewBox="0 0 100 100"
                            >
                                {chartData.map((item, idx) => {
                                    const isHovered = hoveredIdx === idx;

                                    // Posições X
                                    const startX = slotWidth * idx + (slotWidth - (2 * barWidth + gap)) / 2;
                                    const newBarX = startX;
                                    const cancelledBarX = startX + barWidth + gap;

                                    // Alturas Y
                                    const newBarHeight = (item.newClients / maxVal) * 100;
                                    const newBarY = 100 - newBarHeight;

                                    const cancelledBarHeight = (item.cancelledClients / maxVal) * 100;
                                    const cancelledBarY = 100 - cancelledBarHeight;

                                    return (
                                        <g key={idx}>
                                            {/* Retângulo de Background do Slot no Hover */}
                                            {isHovered && (
                                                <rect
                                                    x={slotWidth * idx}
                                                    y="0"
                                                    width={slotWidth}
                                                    height="100"
                                                    fill={COLORS.hoverBg}
                                                    style={{ transition: "fill 0.2s ease" }}
                                                />
                                            )}

                                            {/* Barra de Novos Clientes */}
                                            <rect
                                                x={newBarX}
                                                y={newBarY}
                                                width={barWidth}
                                                height={newBarHeight}
                                                fill={COLORS.new}
                                                rx="2.0"
                                                ry="2.0"
                                                style={{
                                                    transition: "opacity 0.2s ease",
                                                    opacity: hoveredIdx !== null && !isHovered ? 0.7 : 1
                                                }}
                                            />

                                            {/* Barra de Cancelamentos */}
                                            <rect
                                                x={cancelledBarX}
                                                y={cancelledBarY}
                                                width={barWidth}
                                                height={cancelledBarHeight}
                                                fill={COLORS.cancelled}
                                                rx="2.0"
                                                ry="2.0"
                                                style={{
                                                    transition: "opacity 0.2s ease",
                                                    opacity: hoveredIdx !== null && !isHovered ? 0.7 : 1
                                                }}
                                            />
                                        </g>
                                    );
                                })}
                            </svg>

                            {/* Eixo Interativo para Hover e Tooltips */}
                            {chartData.map((item, idx) => {
                                const isHovered = hoveredIdx === idx;
                                const netAcquisition = item.newClients - item.cancelledClients;

                                return (
                                    <div
                                        key={idx}
                                        className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                        onMouseEnter={() => setHoveredIdx(idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                        style={{ cursor: "pointer", zIndex: 5 }}
                                    >
                                        {/* Tooltip flutuante */}
                                        {isHovered && (
                                            <div
                                                className="position-absolute bg-white shadow-lg rounded-3 p-3 text-start animate__animated animate__fadeInUp animate__faster"
                                                style={{
                                                    top: "-42px",
                                                    width: "180px",
                                                    left: "55%",
                                                    transform: "translateX(-50%)",
                                                    pointerEvents: "none",
                                                    zIndex: 9999,
                                                    border: "1.5px solid #482712"
                                                }}
                                            >
                                                <h6 className="fw-bold mb-2 pb-1.5 border-bottom text-center text-uppercase font-size-11" style={{ color: "#482712" }}>
                                                    {item.month}
                                                </h6>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small className="text-muted">Novos Contratos</small>
                                                    <span className="fw-bold font-size-12" style={{ color: COLORS.new }}>+{item.newClients}</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <small className="text-muted">Cancelamentos</small>
                                                    <span className="fw-bold font-size-12" style={{ color: COLORS.cancelled }}>-{item.cancelledClients}</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center pt-1.5 mt-1 border-top">
                                                    <small className="fw-bold" style={{ color: "#482712" }}>Saldo Líquido</small>
                                                    <span className={`fw-bold font-size-12 ${netAcquisition >= 0 ? "text-success" : "text-danger"}`}>
                                                        {netAcquisition >= 0 ? `+${netAcquisition}` : netAcquisition}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Label do mês no Eixo X */}
                                        <span
                                            className="position-absolute fw-bold transition-all"
                                            style={{
                                                bottom: "-45px",
                                                fontSize: "10px",
                                                color: isHovered ? COLORS.new : "#482712",
                                                letterSpacing: "0.2px"
                                            }}
                                        >
                                            {item.month}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

ClientAcquisitionChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        month: PropTypes.string.isRequired,
        newClients: PropTypes.number.isRequired,
        cancelledClients: PropTypes.number.isRequired
    }))
};

export default ClientAcquisitionChart;
